import React, { useEffect } from "react";
import { ArrowRight, ChevronRight, Zap, Award, Sun, TrendingUp, Handshake, Thermometer, Map } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { LogoStrip } from "@/components/site/LogoStrip";
import { Prozess } from "@/components/site/Prozess";
import { CaseCards } from "@/components/site/CaseCards";
import { StelleBesetzenForm } from "@/components/site/StelleBesetzenForm";
import { Ansprechpartner } from "@/components/site/Ansprechpartner";
import { Faq } from "@/components/site/Faq";
import { FlowLines } from "@/components/site/FlowLines";
import { AccentH2, Container, Label, Reveal, Section } from "@/components/site/Primitives";
import { Toaster } from "@/components/ui/sonner";
import personal from "@/data/personal.json";

export const ICONS = { Zap, Award, Sun, TrendingUp, Handshake, Thermometer, Map };

const { hub, rollen } = personal;

export const RollenGrid = ({ exclude = [], h2 = hub.rollen.h2, intro = hub.rollen.intro, label = hub.rollen.label }) => (
  <Section id="rollen" testId="personal-rollen" className="has-lines overflow-hidden">
    <FlowLines color="#0F9D6E" opacity={[0.035, 0.028]} />
    <Container>
      <Reveal>
        <Label>{label}</Label>
        <AccentH2 className="mt-6 max-w-[840px] font-bold text-[#1e2532]">{h2}</AccentH2>
        <p className="mt-6 max-w-[720px] text-[17px] leading-[1.8] text-[#565f70]">{intro}</p>
      </Reveal>
      <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-7">
        {Object.entries(rollen)
          .filter(([slug]) => !exclude.includes(slug))
          .map(([slug, r]) => {
            const Icon = ICONS[r.icon] || Zap;
            return (
              <a
                key={slug}
                href={`/personalvermittlung/${slug}/`}
                data-testid={`rolle-card-${slug}`}
                className="card-hover group flex h-full flex-col rounded-[16px] border border-[#dfe2e0] bg-white p-7 md:p-8"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-[12px] border border-[#0f9d6e]/25 bg-[#f2f8f5]">
                  <Icon size={21} className="text-[#0b7d57]" strokeWidth={1.9} />
                </span>
                <h3 className="mt-6 text-[19px] font-semibold leading-[1.3] text-[#1e2532]">{r.navLabel}</h3>
                <p className="mt-4 text-[15.5px] leading-[1.72] text-[#565f70]">{r.tileText}</p>
                <span className="mt-auto inline-flex items-center gap-2 pt-6 text-[13px] font-bold uppercase tracking-[0.08em] text-[#1e2532]">
                  Zur Rolle <ArrowRight size={15} />
                </span>
              </a>
            );
          })}
      </div>
    </Container>
  </Section>
);

const Warum = () => (
  <Section testId="personal-warum" className="has-lines overflow-hidden bg-[#f5f6f4]">
    <FlowLines color="#0F9D6E" opacity={[0.055, 0.045]} />
    <Container>
      <Reveal>
        <Label>{hub.warum.label}</Label>
        <AccentH2 className="mt-6 max-w-[800px] font-bold text-[#1e2532]">{hub.warum.h2}</AccentH2>
        <p className="mt-6 max-w-[720px] text-[17px] leading-[1.8] text-[#565f70]">{hub.warum.intro}</p>
      </Reveal>
      <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-7">
        {hub.warum.cards.map((cd, i) => (
          <div
            key={cd.h3}
            data-testid={`personal-warum-${i}`}
            className="card-hover h-full rounded-[16px] border border-[#dfe2e0] bg-white p-7 md:p-8"
          >
            <span className="font-display text-[13px] font-bold tracking-[0.14em] text-[#0f9d6e]">
              {String(i + 1).padStart(2, "0")}
            </span>
            <h3 className="mt-4 text-[19px] font-semibold leading-[1.3] text-[#1e2532]">{cd.h3}</h3>
            <span className="accent-rule mt-4" aria-hidden="true" />
            <p className="mt-4 text-[15.5px] leading-[1.74] text-[#565f70]">{cd.text}</p>
          </div>
        ))}
      </div>
    </Container>
  </Section>
);

const Einbettung = () => (
  <Section testId="personal-einbettung" className="has-lines overflow-hidden">
    <FlowLines color="#0F9D6E" opacity={[0.035, 0.028]} />
    <Container>
      <Reveal>
        <Label>{hub.einbettung.label}</Label>
        <AccentH2 className="mt-6 max-w-[800px] font-bold text-[#1e2532]">{hub.einbettung.h2}</AccentH2>
        <p className="mt-6 max-w-[720px] text-[17px] leading-[1.8] text-[#565f70]">{hub.einbettung.intro}</p>
      </Reveal>
      <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4 lg:gap-7">
        {hub.einbettung.links.map((l) => (
          <a
            key={l.href}
            href={l.href}
            data-testid={`personal-einbettung-${l.href}`}
            className="card-hover flex h-full flex-col rounded-[16px] border border-[#dfe2e0] bg-white p-7"
          >
            <h3 className="text-[17.5px] font-semibold leading-[1.35] text-[#1e2532]">{l.title}</h3>
            <p className="mt-3 text-[15px] leading-[1.7] text-[#565f70]">{l.text}</p>
            <span className="mt-auto inline-flex items-center gap-2 pt-5 text-[13px] font-bold uppercase tracking-[0.08em] text-[#1e2532]">
              Ansehen <ArrowRight size={15} />
            </span>
          </a>
        ))}
      </div>
    </Container>
  </Section>
);

export default function PersonalHub() {
  useEffect(() => {
    document.title = hub.metaTitle;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", hub.metaDescription);
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Header />
      <main data-testid="personal-hub-main">
        <section data-testid="personal-hero" className="has-lines relative overflow-hidden bg-[#1e2532] text-white on-dark">
          <FlowLines />
          <div className="hero-pad mx-auto grid max-w-[1240px] grid-cols-1 items-center gap-9 px-6 md:px-10 lg:grid-cols-[1.12fr_0.88fr] lg:gap-12">
            <div>
              <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-1.5 text-[13px] text-white/50">
                <a href="/" className="hover:text-white">Startseite</a>
                <ChevronRight size={13} />
                <span className="text-white/80">Personal</span>
              </nav>
              <Label>{hub.kicker}</Label>
              <h1 className="mt-7 font-bold" data-testid="personal-h1">
                Personalvermittlung für die Energiebranche:{" "}
                <span style={{ color: "var(--accent)" }}>
                  Fachkräfte und Vertrieb für Photovoltaik, Wärmepumpe und Batteriespeicher
                </span>
              </h1>
              <p className="mt-6 max-w-[620px] text-[16.5px] leading-[1.72] text-white/72">{hub.subline}</p>
              <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
                <a
                  href="#formular"
                  data-testid="personal-primary-cta"
                  className="btn-solid inline-flex items-center px-7 py-[15px] text-[13.5px] font-bold uppercase tracking-[0.06em]"
                >
                  <span>{hub.primaryCta}</span>
                </a>
                <a href="#rollen" data-testid="personal-secondary-cta" className="inline-flex items-center gap-2.5 text-[15px] font-semibold text-white/90">
                  <span className="link-underline">{hub.secondaryCta}</span>
                  <ArrowRight size={17} />
                </a>
              </div>
            </div>
            <div className="img-frame media-hero w-full">
              <img src={hub.heroImage} alt={hub.heroImageAlt} width="1264" height="848" fetchPriority="high" decoding="async" className="h-full w-full" />
              <div className="pointer-events-none absolute inset-0 rounded-[6px] border border-white/10" />
            </div>
          </div>
        </section>

        <LogoStrip />
        <RollenGrid />
        <Warum />
        <Prozess />
        <CaseCards cases={hub.cases} />
        <Einbettung />
        <Faq items={hub.faq.items} h2={hub.faq.h2} label="FAQ" />
        <Ansprechpartner />
        <StelleBesetzenForm />
      </main>
      <Footer />
      <Toaster position="bottom-right" />
    </>
  );
}
